const DB_NAME = 'runpixie-upload-manager';
const DB_VERSION = 1;
const STORE_NAME = 'upload-sessions';

function openDatabase() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('INDEXED_DB_UNAVAILABLE'));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'uploadId' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('INDEXED_DB_OPEN_FAILED'));
  });
}

function runRequest(mode, executor) {
  return openDatabase().then((db) => new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, mode);
    const store = transaction.objectStore(STORE_NAME);
    let request;

    try {
      request = executor(store);
    } catch (error) {
      db.close();
      reject(error);
      return;
    }

    transaction.oncomplete = () => {
      db.close();
      resolve(request?.result);
    };
    transaction.onerror = () => {
      const error = transaction.error || request?.error || new Error('INDEXED_DB_TRANSACTION_FAILED');
      db.close();
      reject(error);
    };
    transaction.onabort = () => {
      const error = transaction.error || new Error('INDEXED_DB_TRANSACTION_ABORTED');
      db.close();
      reject(error);
    };
  }));
}

function normalizeSession(session) {
  return {
    ...session,
    updatedAt: Date.now(),
  };
}

async function saveSession(session) {
  const normalized = normalizeSession(session);

  try {
    await runRequest('readwrite', (store) => store.put(normalized));
  } catch (error) {
    // Some browsers cannot structured-clone File System Access handles into IndexedDB.
    // Persist the manifest without the handle so manual re-selection still works.
    if (normalized.directoryHandle) {
      const fallback = {
        ...normalized,
        directoryHandle: null,
      };
      await runRequest('readwrite', (store) => store.put(fallback));
      return fallback;
    }
    throw error;
  }

  return normalized;
}

async function getSession(uploadId) {
  return runRequest('readonly', (store) => store.get(uploadId));
}

async function getAllSessions() {
  const result = await runRequest('readonly', (store) => store.getAll());
  return Array.isArray(result) ? result : [];
}

async function getLatestIncompleteSession(userId = null) {
  const sessions = await getAllSessions();
  const incompleteStatuses = new Set(['active', 'failed', 'saved_for_later']);

  return sessions
    .filter((session) => incompleteStatuses.has(session.status))
    .filter((session) => userId === null || String(session.userId) === String(userId))
    .sort((a, b) => Number(b.updatedAt || 0) - Number(a.updatedAt || 0))[0] || null;
}

async function deleteSession(uploadId) {
  return runRequest('readwrite', (store) => store.delete(uploadId));
}

export default {
  saveSession,
  getSession,
  getLatestIncompleteSession,
  deleteSession,
};
